import * as XLSX from 'xlsx';

/**
 * Export data to a styled Excel (.xlsx) file with a centered title, spacing, and font styling.
 * @param data Array of objects to export
 * @param headers Column headers
 * @param filename Desired file name (no extension)
 * @param mapFunction Function to map each item into a row array
 * @param title Optional centered title (e.g., Event title)
 */
export function exportToExcel<T>(
  data: T[],
  headers: string[],
  filename: string,
  mapFunction: (item: T) => (string | number | boolean)[],
  title?: string
) {
  if (!data || data.length === 0) throw new Error('No data to export');

  const colCount = headers.length;
  const wsData: (string | number | null)[][] = [];

  // Add centered title row
  if (title) {
    wsData.push([`Event: ${title}`]);
    
    // Add 1 blank row after title
    wsData.push(new Array(colCount).fill(null));
  }

  // Add column headers
  wsData.push(headers);

  // Add data rows
  data.forEach(item => {
    wsData.push(mapFunction(item));
  });

  // Generate sheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Merge title across the row if title exists
  if (title) {
    ws['!merges'] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: colCount - 1 }
      }
    ];
  }

  // Apply styles
  const range = XLSX.utils.decode_range(ws['!ref']!);
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      if (R === 0 && title) {
        // Title row
        cell.s = {
          font: { sz: 16, bold: true },
          alignment: { horizontal: 'center', vertical: 'center' }
        };
      } else if ((title && R === 2) || (!title && R === 0)) {
        // Header row
        cell.s = {
          font: { sz: 12, bold: true },
          alignment: { horizontal: 'center' },
          fill: { fgColor: { rgb: "EFEFEF" } }
        };
      } else if ((title && R > 2) || (!title && R > 0)) {
        // Data rows
        cell.s = {
          font: { sz: 11 },
          alignment: { horizontal: 'left' }
        };
      }
    }
  }

  // Create workbook and add the sheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Event');
  
  // Use the event name in the filename if available
  const finalFilename = title 
    ? `${title.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}`
    : `${filename}-${new Date().toISOString().split('T')[0]}`;
    
  XLSX.writeFile(wb, `${finalFilename}.xlsx`);
}